"use client"

import { useState, useRef, useEffect } from "react"
import { Plus, MoreHorizontal, Search, Info, Check } from "lucide-react"
import TempleteModal from "./templete-modal"

export default function TemplatesManagement({ showToast, templeteList }) {
  const [dropdownOpen, setDropdownOpen] = useState(null)
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false)
  const [templateToEdit, setTemplateToEdit] = useState(null)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")

  const dropdownRef = useRef(null)

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(null)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Process template content for display
  const processTemplateContent = (content) => {
    if (!content) return ""

    // Replace {{1}}, {{2}}, etc. with sample values
    // Also handle the *{{1}}* format from the API
    return content.replace(/\*?\{\{(\d+)\}\}\*?/g, (match, number) => {
      const sampleValues = {
        1: "John",
        2: "Tomorrow",
        3: "3:00 PM",
        4: "$99.99",
        5: "Product Name",
      }

      // If the match has asterisks (like *{{1}}*), keep them for formatting
      if (match.startsWith("*") && match.endsWith("*")) {
        return `*${sampleValues[number] || match}*`
      }

      return sampleValues[number] || match
    })
  }

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template)
    showToast("Template selected", `${template.name} template is ready to use.`)
  }

  // Filter templates based on search query
  const filteredTemplates =
    templeteList?.templates?.filter(
      (template) =>
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.language.toLowerCase().includes(searchQuery.toLowerCase()),
    ) || []

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-[#001B3D]">WhatsApp Templates</h2>
          <button
            onClick={() => setShowCreateTemplateModal(true)}
            className="px-4 py-2.5 bg-[#001B3D] text-white rounded-md hover:bg-[#002857] transition-all duration-200 flex items-center shadow-sm hover:shadow"
          >
            <Plus className="w-4 h-4 mr-2" /> Create Template
          </button>
        </div>
        <p className="text-gray-500 mb-6">Create and manage templates for your WhatsApp campaigns</p>

        {/* Search bar */}
        <div className="relative mb-6">
          <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search templates by name, category or language"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#001B3D] focus:border-transparent transition-colors"
          />
        </div>

        {/* Templates Table */}
        <div className="overflow-x-auto rounded-md border border-gray-200 mb-6">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-3.5 text-left text-sm font-medium text-gray-700">
                  Name
                </th>
                <th scope="col" className="px-4 py-3.5 text-left text-sm font-medium text-gray-700">
                  Category
                </th>
                <th scope="col" className="px-4 py-3.5 text-left text-sm font-medium text-gray-700">
                  Language
                </th>
                <th scope="col" className="px-4 py-3.5 text-left text-sm font-medium text-gray-700">
                  Content
                </th>
                <th scope="col" className="px-4 py-3.5 text-left text-sm font-medium text-gray-700">
                  Status
                </th>
                <th scope="col" className="px-4 py-3.5 text-left text-sm font-medium text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredTemplates.length > 0 ? (
                filteredTemplates.map((template) => (
                  <tr key={template.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{template.name}</td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      <span className="inline-flex items-center rounded-full bg-[#001B3D] bg-opacity-10 px-2.5 py-0.5 text-xs font-medium text-[#001B3D]">
                        {template.category}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">{template.language}</td>
                    <td className="px-4 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {template.components?.find((comp) => comp.type === "BODY")?.text
                        ? processTemplateContent(template.components.find((comp) => comp.type === "BODY").text)
                        : "No content available"}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          template.status === "APPROVED"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {template.status === "APPROVED" && <Check className="w-3 h-3 mr-1" />}
                        {template.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-2">
                        <button
                          className="text-[#001B3D] hover:text-[#002857] font-medium transition-colors"
                          onClick={() => {
                            setTemplateToEdit(template)
                            setShowCreateTemplateModal(true)
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="text-[#001B3D] hover:text-[#002857] transition-colors"
                          onClick={() => handleTemplateSelect(template)}
                        >
                          Preview
                        </button>
                        <div className="relative" ref={dropdownOpen === template.id ? dropdownRef : null}>
                          <button
                            onClick={() => setDropdownOpen(dropdownOpen === template.id ? null : template.id)}
                            className="text-gray-400 hover:text-gray-500 transition-colors"
                          >
                            <MoreHorizontal className="w-5 h-5" />
                          </button>

                          {dropdownOpen === template.id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200 animate-[fadeIn_0.2s_ease-in-out]">
                              <div className="py-1">
                                <button
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                  onClick={() => {
                                    setDropdownOpen(null)
                                    setTemplateToEdit(template)
                                    setShowCreateTemplateModal(true)
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                  onClick={() => {
                                    setDropdownOpen(null)
                                    showToast("Template duplicated", `${template.name} template has been duplicated.`)
                                  }}
                                >
                                  Duplicate
                                </button>
                                <button
                                  className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors"
                                  onClick={() => {
                                    setDropdownOpen(null)
                                    showToast("Template deleted", `${template.name} template has been deleted.`)
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-4 py-4 text-center text-sm text-gray-500">
                    {searchQuery ? (
                      <div className="flex flex-col items-center py-4">
                        <Info className="w-6 h-6 text-gray-400 mb-2" />
                        <p>No templates found matching "{searchQuery}"</p>
                        <button className="mt-2 text-[#001B3D] hover:underline" onClick={() => setSearchQuery("")}>
                          Clear search
                        </button>
                      </div>
                    ) : (
                      "No templates found"
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Update the template count display at the bottom */}
        <div className="flex justify-between items-center mt-6">
          <p className="text-sm text-gray-500">
            Showing {filteredTemplates.length} {filteredTemplates.length === 1 ? "template" : "templates"}
            {searchQuery && templeteList?.templates?.length > 0 && ` (filtered from ${templeteList.templates.length})`}
          </p>
          <a href="#" className="text-sm font-medium text-[#001B3D] hover:underline transition-colors">
            View all templates
          </a>
        </div>
      </div>

      {/* Create Template Modal */}
      {showCreateTemplateModal && (
        <TempleteModal
          setShowCreateTemplateModal={setShowCreateTemplateModal}
          templateToEdit={templateToEdit}
          onClose={() => setTemplateToEdit(null)}
        />
      )}
    </div>
  )
}

